import {
  Component, ElementRef, HostListener, Inject, PLATFORM_ID, computed, effect, inject, input, signal,
  untracked, viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { I18nService } from '../../../../Shared/i18n/i18n.service';
import { InstallAppService } from '../../../../Shared/install-app';
import { GetAppStateService } from '../../../../Shared/get-app';
import { PLAY_STORE_URL, isIosDevice } from '../../../../Shared/app-store-links';
import { BrandLogoDirective } from '../../../../Shared/brand-logo/brand-logo.directive';
import { LocationService } from '../../data/location.service';
import { AuthModalService } from '../../data/auth-modal.service';
import { ArrivalAlertService } from '../../data/arrival-alert.service';
import { NearbyShowcaseService } from '../../data/nearby-showcase.service';

/** How long the shopper browses before the popup appears — after the shops have had time to show. */
const DELAY_MS = 4000;

/** The pages it may appear on: browsing, never the middle of a cart, a checkout or an order. */
const BROWSE_PAGE = /^\/(discover|stores)(?:[/?#]|$)/;

/** Elements whose keystrokes are the shopper's own, and must not be interrupted. */
const TYPING = /^(INPUT|TEXTAREA|SELECT)$/;

/** Where Tab may land inside the dialog, in document order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Shops pictured on the drawn phone — one screenful of the listing, no more. */
const SHOPS_SHOWN = 4;

/**
 * "Download our app now!" — a popup offering the Play Store app, on the desktop site and on an Android
 * browser. On a desktop it also carries the QR, which the shopper scans with their phone; on a phone it
 * becomes a bottom sheet with the store buttons alone.
 *
 * Not on an iPhone or iPad: there is no App Store listing yet, and a popup that cannot deliver is only
 * an interruption. Never inside the installed web app.
 *
 * It waits its turn. Only on the discover and stores pages, only once a location is set (a first-time
 * visitor picks one first and sees their shops), never over the location picker or the sign-in modal,
 * and only after {@link DELAY_MS} of that. At most once per page load. Closing it keeps it away for a
 * week; going to the store from it, for a month (see {@link GetAppStateService}).
 *
 * Render this ONCE, at the layout root, like the other overlays — outside the sticky header's stacking
 * context.
 */
@Component({
  selector: 'app-get-app-modal',
  standalone: true,
  imports: [CommonModule, BrandLogoDirective],
  templateUrl: './get-app-modal.html',
  styleUrls: ['./get-app-modal.css'],
})
export class GetAppModalComponent {
  protected readonly i18n = inject(I18nService);
  private readonly state = inject(GetAppStateService);
  private readonly install = inject(InstallAppService);
  private readonly loc = inject(LocationService);
  private readonly authModal = inject(AuthModalService);
  private readonly arrival = inject(ArrivalAlertService);
  private readonly router = inject(Router);

  private readonly showcase = inject(NearbyShowcaseService);

  protected readonly playStoreUrl = PLAY_STORE_URL;
  /** Encoded in the QR: that page detects the phone and forwards to the right store. */
  protected readonly getAppUrl = 'https://www.orderyaa.com/get-app';

  /** Pictures that would not load — the next one in line takes the tile instead. */
  private readonly brokenPicture = signal<Record<string, boolean>>({});

  /**
   * The shops pictured inside the drawn phone: the shopper's OWN nearby shops, taken from the
   * listing behind the popup, so the phone shows the real thing and costs no extra request. The
   * same URL the page behind is already showing, so it comes from the browser's cache.
   *
   * Empty — no location yet, no banners, every image broken — leaves the drawn tiles, which is why
   * the template keeps them.
   */
  protected readonly shops = computed(() => {
    const broken = this.brokenPicture();
    // A picture that fails drops out and the next one slides into its place.
    return this.showcase.pictures().filter((p) => !broken[p.key]).slice(0, SHOPS_SHOWN);
  });

  protected onPictureError(key: string): void {
    this.brokenPicture.update((m) => ({ ...m, [key]: true }));
  }

  protected readonly open = signal(false);
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
  private readonly dialog = viewChild<ElementRef<HTMLElement>>('dialog');

  /** This DEVICE could be offered it at all — settled once, in the constructor. */
  private canOffer = false;
  /** Shown already on this page load — it never comes back before a reload. */
  private shown = false;
  /** Where focus was when it opened, so closing puts the keyboard back rather than at the top. */
  private returnFocusTo: HTMLElement | null = null;

  /**
   * Another surface of the layout's own is covering the screen — today the mobile nav drawer. Passed
   * in rather than read from a service because the layout owns that state, and like the location
   * picker it both holds the popup back and calls off one already counting down.
   */
  readonly blocked = input(false);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  private readonly onBrowsePage = computed(() => BROWSE_PAGE.test(this.url()));

  /**
   * Everything that must hold, right now, for the popup to be on screen. Read while it waits AND
   * while it is open, so whatever stops being true takes it away again — see the effects below.
   *
   * The order is deliberate: who may be offered it at all, then whether there is anything worth
   * interrupting for, then everything already on screen that outranks an advertisement.
   */
  private readonly clear = computed(() =>
    this.canOffer
    && !this.state.modalResting()
    && !this.install.installed()
    && this.onBrowsePage()
    && !!this.loc.location()
    // Nothing to come back to: an area with no shops would get "no shops here" under a popup
    // asking them to install the app that would show them the same nothing.
    && this.showcase.hasShops()
    // A rider at the door outranks an advertisement. The alert is a fixed banner at z-index 120
    // and this popup's backdrop is 1000, so without this it would bury the alarm AND its
    // "I'm coming" button — and it both holds the popup back and takes an open one away.
    && !this.arrival.alert()
    && !this.loc.pickerOpen()
    && !this.authModal.isOpen()
    && !this.blocked());

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    if (!isPlatformBrowser(platformId)) return;
    this.canOffer = !!PLAY_STORE_URL && !isIosDevice();

    // The delay restarts whenever the way stops being clear — the drawer or the location picker
    // opening, a page change, the strip's Install being tapped — so the popup never lands on top of
    // something the shopper has just started, nor on a store they are already on their way to.
    effect((onCleanup) => {
      if (!this.clear() || untracked(() => this.open()) || this.shown) return;
      const timer = setTimeout(() => {
        this.shown = true;
        this.open.set(true);
        // After the dialog renders, focus its close button so Escape and Tab work at once — but
        // never out of a box the shopper is typing in, where the next keystrokes would be lost and
        // a space would press ✕.
        setTimeout(() => {
          const busy = document.activeElement;
          if (busy instanceof HTMLElement && (busy.isContentEditable || TYPING.test(busy.tagName))) {
            return;
          }
          this.returnFocusTo = busy instanceof HTMLElement && busy !== document.body ? busy : null;
          this.closeButton()?.nativeElement.focus();
        });
      }, DELAY_MS);
      onCleanup(() => clearTimeout(timer));
    });

    // The way stopping being clear while it is open — the Back button off a browse page, the drift
    // check opening the location picker — takes it away rather than stacking two dialogs. Not a
    // refusal by the shopper, so it does not spend the quiet week that pressing ✕ does.
    effect(() => {
      if (this.open() && !this.clear()) {
        untracked(() => {
          this.open.set(false);
          this.restoreFocus();
        });
      }
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) this.close();
  }

  /**
   * Keep Tab inside the dialog while it is open.
   *
   * It says `aria-modal="true"`, which tells a screen reader the rest of the page is not there.
   * Nothing was enforcing that: one Shift+Tab off the ✕ walked the keyboard straight into the page
   * the dialog had just declared hidden, with the backdrop swallowing the clicks. Angular matches
   * modifiers exactly, so plain Tab and Shift+Tab arrive on separate bindings and neither fires twice.
   */
  @HostListener('document:keydown.tab', ['$event'])
  @HostListener('document:keydown.shift.tab', ['$event'])
  protected onTab(event: Event): void {
    if (!this.open() || !(event instanceof KeyboardEvent)) return;
    const root = this.dialog()?.nativeElement;
    if (!root) return;
    // `offsetParent` is null for a hidden element — the "coming soon" badge is a span and never
    // focusable anyway, but a future `@if` inside the dialog must not create a dead stop.
    const stops = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((el) => el.offsetParent !== null);
    if (!stops.length) return;

    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;
    const outside = !(active instanceof Node) || !root.contains(active);
    if (event.shiftKey ? active === first || outside : active === last || outside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }

  /** Put the keyboard back where it was. Synchronous, before the dialog leaves the DOM. */
  private restoreFocus(): void {
    const el = this.returnFocusTo;
    this.returnFocusTo = null;
    if (el?.isConnected) el.focus();
  }

  protected close(): void {
    this.open.set(false);
    this.state.closeModal();
    this.restoreFocus();
  }

  protected goToStore(): void {
    this.open.set(false);
    this.state.wentToStore();
    this.restoreFocus();
  }
}
